import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  BreadcrumbHeading,
  Plus,
  PrimaryButton,
  Spinner,
  Table,
  TimerIcon,
  Typography,
} from '@weareopenr/component-library';
import MealtimeDetailsModal from 'src/components/MealtimeDetailsModal';
import EmptyState from '../../components/EmptyState';
import { useMealtimes } from '../../hooks/useMealtimes/useMealtimes';
import { groupAndFormatSchedules } from './groupAndFormatSchedules/groupAndFormatSchedules';
import styles from './Mealtimes.module.scss';
import ActionsMenu from 'src/components/ActionsMenu';
import { DeleteMealtimePrompt } from '../../components/DeleteMealtimePrompt/DeleteMealtimePrompt';
import { useMenu } from 'src/hooks/useMenu/useMenu';
import { Routes } from 'src/routes';
import { createColumnHelper } from '@tanstack/react-table';
import { IMealtime } from 'src/types/mealtimes.types';

const MAX_MENU_CATEGORIES = 3;

const ICON = () => <TimerIcon className={styles['empty-state-icon']} size="large" />;

const columnHelper = createColumnHelper<IMealtime>();
export const Mealtimes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { menuId } = useParams<{ menuId: string }>();
  const { data: menu } = useMenu(menuId);
  const { isLoading, data } = useMealtimes(menuId);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [mealTimeToDelete, setMealtimeToDelete] = useState<string | null>();

  const handleModalClick = () => setIsModalOpen((prev: boolean) => !prev);

  const handleMealtimeClick = (id: string): void => {
    navigate(`${location.pathname}/${id}`);
  };

  const handleRowClick = useCallback((row: IMealtime) => handleMealtimeClick(row.mealtime_id), [handleMealtimeClick]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
      }),
      columnHelper.accessor('schedules', {
        header: 'Availability',
        size: 200,
        cell: (info) => {
          const schedules = groupAndFormatSchedules(info.row.original.schedules);
          return schedules.length > 0 ? (
            <div>
              {schedules.map((schedule) => (
                <Typography key={schedule.startDay + schedule.endDay + schedule.time}>
                  {`${schedule.startDay}${schedule.endDay ? ' - ' + schedule.endDay : ''}: ${schedule.time}`}
                </Typography>
              ))}
            </div>
          ) : (
            '-'
          );
        },
      }),
      columnHelper.accessor('categories', {
        header: 'Menu sections',
        cell: (info) =>
          info.getValue().length > 0 ? (
            <div>
              {info
                .getValue()
                .slice(0, MAX_MENU_CATEGORIES)
                .map((category) => (
                  <Typography key={category.category_id}>{category.name}</Typography>
                ))}
              {info.getValue().length > MAX_MENU_CATEGORIES && (
                <Typography weight="semi-bold">+{info.getValue().length - MAX_MENU_CATEGORIES} more</Typography>
              )}
            </div>
          ) : (
            '-'
          ),
      }),
      columnHelper.display({
        id: 'actions',
        size: 80,
        cell: (info) => (
          <ActionsMenu
            onView={() => handleMealtimeClick(info.row.original.mealtime_id)}
            onDelete={() => setMealtimeToDelete(info.row.original.mealtime_id)}
          />
        ),
      }),
    ],
    [handleMealtimeClick, setMealtimeToDelete],
  );

  return (
    <>
      {isLoading ? (
        <div className={styles.spinner} data-testid="mealtimes-loading">
          <Spinner size="xlarge" colour="rainbow" />
        </div>
      ) : (
        <>
          <div className={styles['header']}>
            <BreadcrumbHeading
              breadcrumbs={[
                { label: 'Menus', path: Routes.MENUS },
                { label: menu?.name, path: `${Routes.MENUS}/${menu?.menuId}` },
                { label: 'Mealtimes', path: `${Routes.MENUS}/${menu?.menuId}/mealtimes` },
              ]}
              primaryButton={
                <PrimaryButton icon={Plus} onClick={handleModalClick}>
                  Add Mealtime
                </PrimaryButton>
              }
            />
          </div>
          {data?.length > 0 ? (
            <Table
              columns={columns}
              data={data}
              isRowClickable={({ mealtime_id }) => (mealtime_id ? handleRowClick : false)}
            />
          ) : (
            <>
              <Typography size="large" className={styles['mealtime-info']}>
                If no specific mealtimes are added, the venue&apos;s opening hours will be used for menu availability
              </Typography>
              <EmptyState
                emptySectionText="Create your first mealtime"
                Icon={ICON}
                buttonText="Create"
                onClick={handleModalClick}
              />
            </>
          )}
          <DeleteMealtimePrompt
            mealtimeId={mealTimeToDelete}
            menuId={menuId}
            closePrompt={() => setMealtimeToDelete(null)}
          />
          <MealtimeDetailsModal
            isModalOpen={isModalOpen}
            onModalClose={handleModalClick}
            menuId={menu?.menuId}
            mealtimes={data}
          />
        </>
      )}
    </>
  );
};
